import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import RadarViz from "@/components/RadarViz";
import { Lock, Share2, Sparkles, Brain, HeartHandshake, Gauge, Briefcase, Palette, Zap, Mail, BookOpen } from "lucide-react";
import { toast } from "sonner";

const ARCHETYPE_NAMES = {
  SYSTEMS_THINKER: "The Systems Thinker",
  STRATEGIST: "The Strategist",
  NAVIGATOR: "The Navigator",
  CONNECTOR: "The Connector",
  ANALYST: "The Analyst",
  ADAPTIVE_THINKER: "The Adaptive Thinker",
  OBSERVER: "The Observer",
  BALANCED_THINKER: "The Balanced Thinker",
};

export default function Results() {
  const { aid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [iq, setIq] = useState(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{data: s}, {data: p}] = await Promise.all([
          api.get(`/assessment/${aid}/summary`),
          api.get("/pricing"),
        ]);
        setData(s); setPricing(p);
      } catch (e) {
        if (e?.response?.status === 401) nav("/auth");
      }
    })();
  }, [aid, nav]);

  useEffect(() => {
    if (data?.unlocked && !explanation) {
      api.get(`/assessment/${aid}/explain`).then(r => setExplanation(r.data.explanation)).catch(()=>{});
    }
    if (data?.unlocked && !iq) {
      api.get(`/assessment/${aid}/iq-estimate`).then(r => setIq(r.data)).catch(()=>{});
    }
  }, [data?.unlocked, aid, explanation, iq]);

  const unlock = async () => {
    setCheckingOut(true);
    try {
      const { data: co } = await api.post("/payments/checkout", {
        assessment_id: aid, origin_url: window.location.origin,
      });
      window.location.href = co.checkout_url;
    } catch { toast.error("Checkout failed"); setCheckingOut(false); }
  };

  const redeem = async () => {
    const c = (code || "").trim();
    if (!c) return toast.info("Enter a code first");
    setRedeeming(true);
    try {
      await api.post("/codes/redeem", { code: c, assessment_id: aid });
      toast.success("Code redeemed — your complete profile is unlocked");
      // Refresh the summary so the paywall disappears and the full profile loads
      const { data: s } = await api.get(`/assessment/${aid}/summary`);
      setData(s);
      setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally { setRedeeming(false); }
  };

  const share = () => {
    nav(`/share/${aid}`);
  };

  const sendWeekly = async () => {
    try {
      const { data } = await api.post("/email/send-weekly", { assessment_id: aid, origin_url: window.location.origin });
      toast.success(`Weekly report sent to ${data.recipient}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not send";
      if (detail.includes("No recipient")) toast.error("Add an email to your account first");
      else toast.error(detail);
    }
  };

  if (!data) return <div><Nav /><div className="max-w-xl mx-auto px-5 py-20 text-slate-400">Loading your DualQ…</div></div>;

  if (data.status && data.status !== "completed") {
    return <div><Nav /><div className="max-w-xl mx-auto px-5 py-20 text-slate-400" data-testid="results-not-ready">Assessment not complete yet.</div></div>;
  }

  const price = pricing?.result_unlock_price ?? 1.99;
  const full = data.full_profile;

  return (
    <div><Nav />
      <div className="max-w-5xl mx-auto px-5 py-8">
        <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-3 fade-up">Your DualQ</div>
        <h1 className="font-serif text-4xl sm:text-6xl leading-[1.05] tracking-tight mb-2 fade-up" data-testid="archetype-name">
          {ARCHETYPE_NAMES[data.archetype]}
        </h1>
        <p className="text-slate-300 max-w-2xl fade-up" data-testid="archetype-description">{data.archetype_description}</p>

        <div className="mt-8 grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3">
          <StatCard icon={Brain} color="#38BDF8" label="Cognitive Index" value={data.cognitive_teaser?.score ?? "—"} />
          <StatCard icon={HeartHandshake} color="#F59E0B" label="Emotional Index" value={data.emotional_teaser?.score ?? "—"} />
          <StatCard icon={Gauge} color="#A855F7" label="Evidence Coverage" value={`${data.profile_confidence?.score ?? 0}%`} />
        </div>
        <p className="mt-3 text-[11px] text-slate-500 font-mono" data-testid="provisional-disclaimer">
          PROVISIONAL · Evidence coverage reflects how much data we have, not statistical confidence.
          Items are EXPERIMENTAL and not yet normed against standardized tests.
        </p>

        {!data.unlocked && (
          <div className="mt-10 rounded-3xl bg-gradient-to-br from-[#0E172A] to-[#131E36] border border-[#E0A96D]/25 p-8 sm:p-10 relative overflow-hidden" data-testid="paywall-block">
            <div className="absolute top-0 right-0 w-[280px] h-[280px] rounded-full bg-[#E0A96D]/10 blur-[100px]" />
            <div className="relative">
              <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-3">Your complete DualQ is ready</div>
              <h2 className="font-serif text-3xl sm:text-4xl mb-3">Unlock your full profile</h2>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-slate-300 mb-6">
                {["DualQ radar visualization","All cognitive + emotional domain scores","Head vs Heart analysis","Calibration score","Career recommendations","Hobby recommendations","AI-personalized profile explanation","Shareable DualQ card"].map(x=>(
                  <li key={x} className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-[#E0A96D]" />{x}</li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <button onClick={unlock} disabled={checkingOut}
                  className="inline-flex items-center gap-2 px-6 py-4 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B] transition-colors disabled:opacity-60"
                  data-testid="unlock-btn">
                  <Lock className="w-4 h-4" /> Unlock my complete DualQ · ${price.toFixed(2)}
                </button>
                <button onClick={()=>nav("/dashboard")} className="text-slate-400 hover:text-slate-200 text-sm px-2 py-2" data-testid="not-now-btn">Not now</button>
              </div>
              <p className="text-xs text-slate-500 mt-3 font-mono">One-time payment. No subscription. Your result stays unlocked.</p>

              <div className="mt-6 pt-5 border-t border-slate-800/60">
                <div className="text-xs uppercase font-mono tracking-widest text-slate-500 mb-2">Have an access code?</div>
                <div className="flex gap-2 flex-col sm:flex-row">
                  <input
                    type="text"
                    value={code}
                    onChange={e => setCode(e.target.value.toUpperCase())}
                    placeholder="ENTER CODE"
                    className="flex-1 bg-[#0E172A] border border-slate-700 focus:border-[#E0A96D]/60 outline-none text-slate-100 font-mono tracking-widest rounded-lg px-3 py-2 text-sm"
                    data-testid="code-input"
                  />
                  <button
                    onClick={redeem}
                    disabled={redeeming || !code.trim()}
                    className="px-5 py-2 rounded-lg border border-[#E0A96D]/50 text-[#F0C28B] hover:bg-[#E0A96D]/10 disabled:opacity-50"
                    data-testid="redeem-code-btn"
                  >
                    {redeeming ? "Redeeming…" : "Redeem"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {data.unlocked && full && (
          <div className="mt-10 space-y-6" data-testid="full-profile">
            <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-6 sm:p-8">
              <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-3">The DualQ Visualization</div>
              <RadarViz domainScores={full.domain_scores} />
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Panel icon={Brain} color="#38BDF8" title="Cognitive domains">
                <DomainList scores={full.domain_scores} layer="cognitive" />
              </Panel>
              <Panel icon={HeartHandshake} color="#F59E0B" title="Emotional / social domains">
                <DomainList scores={full.domain_scores} layer="emotional" />
              </Panel>
            </div>

            <Panel icon={Gauge} color="#A855F7" title="Head vs Heart & Calibration">
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                <Metric label="Cognitive Index" value={full.cognitive_index?.score} />
                <Metric label="Emotional Index" value={full.emotional_index?.score} />
                <Metric label="Calibration" value={full.calibration?.calibration_score ?? "—"} />
              </div>
              <p className="text-xs text-slate-500 mt-3 font-mono">Calibration is how well your confidence matched your actual accuracy. Higher = better self-knowledge under uncertainty.</p>
            </Panel>

            <Panel icon={Sparkles} color="#E0A96D" title="Personalized explanation">
              <p className="text-slate-300 leading-relaxed whitespace-pre-line" data-testid="ai-explanation">
                {explanation || "Generating your grounded explanation…"}
              </p>
            </Panel>

            <Panel icon={Gauge} color="#E0A96D" title="DualQ Estimated IQ">
              {!iq && <p className="text-slate-400 text-sm">Checking IQ estimation status…</p>}
              {iq && iq.status !== "Available" && (
                <div className="space-y-3" data-testid="iq-pending">
                  <div className="font-serif text-2xl text-amber-200">Estimated IQ: Pending Validation</div>
                  <p className="text-sm text-slate-400">{iq.iq_scale_disclaimer}</p>
                  <ul className="list-disc list-inside text-xs text-slate-500 space-y-1">
                    {(iq.reasons || []).map((r,i)=><li key={i}>{r}</li>)}
                  </ul>
                  <div className="text-xs text-slate-500 font-mono pt-2 border-t border-slate-800/60">
                    Items counted (cognitive, calibrated): {iq.n_calibrated_items_used} ·
                    Provisional Cognitive Performance: {iq.provisional_cognitive_performance ?? "—"}
                  </div>
                  <p className="text-[11px] text-slate-500 italic">
                    Emotional intelligence and personality items are NOT included in IQ estimation.
                  </p>
                </div>
              )}
              {iq && iq.status === "Available" && (
                <div className="space-y-3" data-testid="iq-available">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Metric label="DualQ IQ" value={iq.iq} />
                    <Metric label="Range (95%)" value={`${iq.lower}–${iq.upper}`} />
                    <Metric label="Percentile" value={`${iq.percentile}%`} />
                    <Metric label="Evidence" value={iq.evidence_quality} />
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">{iq.explanation}</p>
                  <div className="text-[11px] font-mono text-slate-500">Norm: {iq.norm_table_name} · n items used: {iq.n_calibrated_items_used}</div>
                </div>
              )}
            </Panel>

            <div className="grid gap-6 md:grid-cols-2">
              <Panel icon={Briefcase} color="#38BDF8" title="Career directions worth exploring">
                <ul className="space-y-2 text-slate-300 text-sm">
                  {full.career_recommendations.map(c => <li key={c} className="flex gap-2"><span className="text-[#38BDF8]">›</span>{c}</li>)}
                </ul>
              </Panel>
              <Panel icon={Palette} color="#F59E0B" title="Hobbies that fit your profile">
                <ul className="space-y-2 text-slate-300 text-sm">
                  {full.hobby_recommendations.map(c => <li key={c} className="flex gap-2"><span className="text-[#F59E0B]">›</span>{c}</li>)}
                </ul>
              </Panel>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
              <button onClick={()=>nav(`/challenge/${aid}`)} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#A855F7] text-white font-semibold hover:bg-[#C084FC]" data-testid="challenge-me-btn">
                <Zap className="w-4 h-4" /> Challenge me
              </button>
              <button onClick={()=>nav(`/report/${aid}`)} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#38BDF8] text-[#070C18] font-semibold hover:bg-[#7DD3FC]" data-testid="advanced-report-btn">
                Advanced report
              </button>
              <button onClick={share} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B]" data-testid="share-btn">
                <Share2 className="w-4 h-4" /> Share my DualQ
              </button>
              <button onClick={sendWeekly} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-200" data-testid="email-weekly-btn">
                <Mail className="w-4 h-4" /> Email me my DualQ
              </button>
              <button onClick={()=>nav(`/review/${aid}`)} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-200" data-testid="review-answers-btn">
                <BookOpen className="w-4 h-4" /> Review your answers
              </button>
              <button onClick={()=>nav("/dashboard")} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-200" data-testid="to-dashboard-btn">
                Go to dashboard
              </button>
            </div>

            <p className="text-xs text-slate-500 font-mono border-t border-slate-800 pt-4">
              DualQ v{full.assessment_version} · scoring v{full.scoring_version} · items marked EXPERIMENTAL. Provisional DualQ performance score — not a clinical IQ or EQ measurement.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({icon:Icon, color, label, value}) {
  return (
    <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-5">
      <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-widest font-mono" style={{color}}>
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="font-serif text-3xl">{value}</div>
    </div>
  );
}
function Panel({icon:Icon, color, title, children}) {
  return (
    <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-6">
      <div className="flex items-center gap-2 mb-4 uppercase font-mono text-xs tracking-widest" style={{color}}>
        <Icon className="w-4 h-4" /> {title}
      </div>
      {children}
    </div>
  );
}
function DomainList({ scores, layer }) {
  const rows = Object.entries(scores).filter(([,v])=>v.layer===layer);
  return (
    <div className="space-y-3" data-testid={`domain-list-${layer}`}>
      {rows.map(([k,v]) => {
        const evidenceLabel = v.evidence_strength === "LOW" ? "Needs more assessment" : v.evidence_strength;
        const evidenceColor = v.evidence_strength === "HIGH" ? "text-emerald-400" :
                              v.evidence_strength === "MODERATE" ? "text-amber-300" : "text-slate-400";
        return (
          <div key={k} className="rounded-xl bg-[#0E172A]/60 p-3 border border-slate-800/60">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[140px] text-sm capitalize text-slate-200">{k.replace(/_/g," ")}</div>
              <div className="flex-1 min-w-[120px] h-2 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full rounded-full" style={{width:`${v.score}%`, background: layer==="cognitive"?"#38BDF8":"#F59E0B"}} />
              </div>
              <div className="w-12 text-right font-mono text-sm text-slate-100">{v.score}</div>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono text-slate-500">
              <span>n={v.items_completed}</span>
              {v.accuracy_pct != null && <span>accuracy {v.accuracy_pct}%</span>}
              {v.avg_response_time_ms != null && <span>~{(v.avg_response_time_ms/1000).toFixed(1)}s</span>}
              <span className={evidenceColor}>{evidenceLabel}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
function Metric({label, value}) {
  return (
    <div className="rounded-xl bg-[#0E172A] p-4">
      <div className="text-xs text-slate-500 uppercase font-mono tracking-widest">{label}</div>
      <div className="font-serif text-3xl mt-1">{value ?? "—"}</div>
    </div>
  );
}
